
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Upload, AlertCircle, Image } from 'lucide-react';
import { uploadLogo, saveLogoUrl } from '@/utils/businessService';

interface LogoUploadProps {
  currentLogo: string;
  onSaved?: () => void;
}

const LogoUpload: React.FC<LogoUploadProps> = ({ currentLogo, onSaved }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(currentLogo || null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // File type validation
      if (!file.type.match('image.*')) {
        setError('Please select an image file (PNG, JPG, JPEG, GIF)');
        return;
      }
      
      // File size validation (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size should be less than 2MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    setError(null);
    
    try {
      const logoUrl = await uploadLogo(selectedFile);
      await saveLogoUrl(logoUrl);
      
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError('Failed to upload logo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Logo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Current Logo
          </label>
          <div className="border border-gray-200 rounded-md p-4 flex items-center justify-center bg-gray-50 h-40">
            {preview ? (
              <img 
                src={preview} 
                alt="Company Logo" 
                className="max-h-32 max-w-full object-contain" 
              />
            ) : (
              <div className="text-center text-gray-400">
                <Image className="mx-auto h-12 w-12 mb-2" />
                <p>No logo uploaded</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Upload New Logo
          </label>
          <Input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
          <p className="text-xs text-gray-500">
            Accepted formats: PNG, JPG, JPEG, GIF. Maximum size: 2MB.
            Recommended dimensions: 200px x 50px.
          </p>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" /> Upload Logo
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default LogoUpload;
